import { z } from "zod";

import {
  DateColumnDisplayFormat,
  DatetimeColumnDisplayFormat,
  NumberColumnDisplayFormat,
} from "../display-table/columnDisplayFormatTypes.js";
import { DisplayTableOutputColumnTypeLiteral } from "../display-table/outputTypes.js";
import { ExploreCustomTimeUnit } from "../explore/exploreCustomTimeUnit.js";
import { getNormalEnum } from "../pseudoEnums.js";

export const PivotFieldInputType = DisplayTableOutputColumnTypeLiteral;
export type PivotFieldInputType = z.infer<typeof PivotFieldInputType>;

export const PivotTruncateDateIntervalLiteral = z.enum([
  "HOUR",
  "DAY",
  "WEEK",
  "MONTH",
  "QUARTER",
  "YEAR",
]);
export type PivotTruncateDateInterval = z.infer<
  typeof PivotTruncateDateIntervalLiteral
>;
export const PivotTruncateDateInterval = getNormalEnum(
  PivotTruncateDateIntervalLiteral,
);

export const PivotDisplayFormat = z.union([
  DateColumnDisplayFormat,
  DatetimeColumnDisplayFormat,
  NumberColumnDisplayFormat,
]);
export type PivotDisplayFormat = z.infer<typeof PivotDisplayFormat>;

export const PivotTruncateToUnit = z.union([
  PivotTruncateDateIntervalLiteral,
  ExploreCustomTimeUnit,
]);
export type PivotTruncateToUnit = z.infer<typeof PivotTruncateToUnit>;
export const PivotGroupByField = z.object({
  field: z.string(),
  fieldType: PivotFieldInputType.optional(),
  nameOverride: z.string().optional(),
  truncateTo: PivotTruncateToUnit.optional(),
  displayFormat: PivotDisplayFormat.optional(),
});
export type PivotGroupByField = z.infer<typeof PivotGroupByField>;

export const PivotAggregationLiteral = z.enum([
  "COUNT",
  "COUNT_DISTINCT",
  "SUM",
  "AVERAGE",
  "MIN",
  "MAX",
  "MEDIAN",
]);
export type PivotAggregation = z.infer<typeof PivotAggregationLiteral>;
export const PivotAggregation = getNormalEnum(PivotAggregationLiteral);

export const PivotValueField = z.object({
  field: z.string(),
  fieldType: PivotFieldInputType.optional(),
  nameOverride: z.string().optional(),
  aggregation: PivotAggregationLiteral,
  displayFormat: PivotDisplayFormat.optional(),
});
export type PivotValueField = z.infer<typeof PivotValueField>;

export const PivotTableConfig = z.object({
  rows: z.array(PivotGroupByField),
  columns: z.array(PivotGroupByField),
  values: z.array(PivotValueField),
  // If true, display multiple values as rows. Ignored unless there are 2+ values
  valuesAsRows: z.boolean().optional(),
});
export type PivotTableConfig = z.infer<typeof PivotTableConfig>;
